import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PlusIcon } from '@heroicons/react/24/outline';
import { builderApi, errorMessage } from '../api';
import { Eyebrow, formatDate, useManageBase } from '../ui';

export default function ManageChallenges() {
    const navigate = useNavigate();
    const { base, area } = useManageBase();
    const [items, setItems] = useState(null);
    const [error, setError] = useState(null);
    const [creating, setCreating] = useState(false);

    const load = () => builderApi.list()
        .then(setItems)
        .catch((err) => setError(errorMessage(err, 'Could not load challenges')));

    useEffect(() => { load(); }, []);

    const create = async () => {
        setCreating(true);
        try {
            const created = await builderApi.create({ title: 'Untitled challenge' });
            navigate(`${base}/${created.id}/edit`);
        } catch (err) {
            toast.error(errorMessage(err, 'Could not create a challenge'));
            setCreating(false);
        }
    };

    const archive = async (item) => {
        const started = item.submissions.in_progress + item.submissions.submitted + item.submissions.approved;
        const warning = started
            ? `Archive "${item.title}"? ${started} intern submission(s) will be hidden from students and mentors.`
            : `Archive "${item.title}"?`;
        if (!window.confirm(warning)) return;
        try {
            await builderApi.archive(item.id);
            toast.success('Challenge archived');
            load();
        } catch (err) {
            toast.error(errorMessage(err, 'Could not archive the challenge'));
        }
    };

    return (
        <div className="mx-auto w-full max-w-6xl space-y-6 pb-12">
            <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <Eyebrow>{area} / Practical challenges</Eyebrow>
                    <h1 className="mt-1.5 text-2xl font-bold tracking-tight text-ink-950 md:text-[1.75rem]">Practical challenges</h1>
                    <p className="mt-1 text-sm text-ink-600">Challenges unlock for interns in this order. Edit one to change its sections, questions and review rules.</p>
                </div>
                <div className="flex gap-2">
                    <Link to="/mentor/challenge-reviews" className="btn-secondary">Reviews queue</Link>
                    <button type="button" onClick={create} disabled={creating} className="btn-primary inline-flex items-center gap-1.5">
                        <PlusIcon className="h-4 w-4" /> {creating ? 'Creating…' : 'New challenge'}
                    </button>
                </div>
            </header>

            {error && <p className="text-sm text-red-700">{error}</p>}

            <div className="overflow-hidden rounded-2xl border border-border bg-white">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[820px] text-left text-sm">
                        <thead className="border-b border-border text-[11px] font-semibold uppercase tracking-wider text-ink-500">
                            <tr>
                                <th className="px-5 py-3">Order</th>
                                <th className="px-5 py-3">Challenge</th>
                                <th className="px-5 py-3">Format</th>
                                <th className="px-5 py-3">Status</th>
                                <th className="px-5 py-3">Interns</th>
                                <th className="px-5 py-3"><span className="sr-only">Actions</span></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {!items && !error && <tr><td colSpan={6} className="px-5 py-4"><div className="h-5 animate-pulse rounded bg-ink-100" /></td></tr>}
                            {items && items.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-ink-500">No challenges yet.</td></tr>}
                            {items && items.map((item) => (
                                <tr key={item.id}>
                                    <td className="px-5 py-3 text-ink-600">{item.order_index}</td>
                                    <td className="px-5 py-3">
                                        <p className="font-semibold text-ink-950">{item.title}</p>
                                        <p className="line-clamp-1 text-xs text-ink-500">{item.summary}</p>
                                    </td>
                                    <td className="px-5 py-3 text-ink-700">
                                        {item.layout === 'workbook' ? 'Workbook' : 'Single page'} · {item.section_count} section{item.section_count === 1 ? '' : 's'} · {item.field_count} questions
                                    </td>
                                    <td className="px-5 py-3">
                                        {item.is_published
                                            ? <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-800">Published</span>
                                            : <span className="rounded-full bg-ink-100 px-2.5 py-1 text-xs font-medium text-ink-600">Draft</span>}
                                        <p className="mt-1 text-[11px] text-ink-500">
                                            Updated {formatDate(item.updated_at)}{item.updated_by_name ? ` by ${item.updated_by_name}` : ''}
                                        </p>
                                        {item.created_by_name && <p className="text-[11px] text-ink-400">Created by {item.created_by_name}</p>}
                                    </td>
                                    <td className="px-5 py-3 text-xs text-ink-700">
                                        {item.submissions.in_progress} working · {item.submissions.submitted} to review · {item.submissions.approved} approved
                                    </td>
                                    <td className="whitespace-nowrap px-5 py-3 text-right">
                                        <Link to={`${base}/${item.id}/edit`} className="btn-secondary mr-2 inline-flex">Edit</Link>
                                        <button type="button" onClick={() => archive(item)} className="text-xs font-medium text-ink-500 hover:text-red-700">Archive</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
